/**
 * Provider-agnostic live transcriber: feed it PCM16 frames, it emits
 * `TranscriptEvent`s. Frames sent before the socket opens are buffered
 * (bounded) and flushed on open, so the capture never has to wait for the
 * grant/handshake.
 *
 * `stop()` asks the vendor to flush trailing results and resolves once the
 * socket closes (or after a bounded wait) — the caller then reads the merged
 * transcript. The bound grows with the audio replayed at open: a reconnect
 * that just pushed a 30 s backlog needs more than the usual flush window.
 * An UNEXPECTED close is reported through `onDrop` so the owner can
 * reconnect with a fresh grant; this module never re-fetches credentials.
 */

import type { SttGrant } from "../../_types/media-upload";
import { openAssemblyAiSocket } from "./assemblyai";
import type { TranscriptEvent } from "./transcript-merge";

const FLUSH_BASE_MS = 5_000;
const FLUSH_MAX_MS = 15_000;
/** Audio held while the socket is still connecting; older frames are dropped. */
const MAX_BUFFERED_MS = 30_000;

export interface LiveTranscriber {
  send: (frame: ArrayBuffer) => void;
  /**
   * Flush and close. Resolves after trailing finals (bounded) with whether the
   * vendor actually finished before the bound — false means the tail of the
   * transcript may be missing. Idempotent.
   */
  stop: () => Promise<boolean>;
}

export interface LiveTranscriberInput {
  grant: SttGrant;
  /** The PCM frames' sample rate — declared to the vendor when the stream opens. */
  sampleRate: number;
  /** Length of one frame, for the buffering and flush bounds. */
  frameMs: number;
}

export interface LiveTranscriberHandlers {
  onEvent: (event: TranscriptEvent) => void;
  onOpen: () => void;
  /** The vendor closed the stream while we still wanted it. */
  onDrop: (reason: string) => void;
}

export function createLiveTranscriber(
  { grant, sampleRate, frameMs }: LiveTranscriberInput,
  handlers: LiveTranscriberHandlers,
): LiveTranscriber {
  const maxBuffered = Math.max(1, Math.floor(MAX_BUFFERED_MS / frameMs));
  const buffered: ArrayBuffer[] = [];
  let replayedMs = 0;
  let replayedAt = 0;
  let open = false;
  let stopped = false;
  let stopping: Promise<boolean> | null = null;
  let resolveClosed: (() => void) | null = null;
  const closed = new Promise<void>((resolve) => {
    resolveClosed = resolve;
  });
  let resolveOpened: (() => void) | null = null;
  const opened = new Promise<void>((resolve) => {
    resolveOpened = resolve;
  });

  // `grant.provider` is the seam: a second vendor is another arm here.
  const socket = openAssemblyAiSocket(grant, sampleRate, {
    onEvent: handlers.onEvent,
    onOpen() {
      open = true;
      replayedMs = buffered.length * frameMs;
      replayedAt = Date.now();
      for (const frame of buffered) socket.send(frame);
      buffered.length = 0;
      resolveOpened?.();
      handlers.onOpen();
    },
    onClose({ clean, code, reason }) {
      open = false;
      resolveOpened?.();
      resolveClosed?.();
      if (!clean && !stopped)
        handlers.onDrop(reason || `socket closed (${code})`);
    },
  });

  return {
    send(frame) {
      if (stopped) return;
      if (open) {
        socket.send(frame);
        return;
      }
      if (buffered.length >= maxBuffered) buffered.shift();
      buffered.push(frame);
    },
    stop() {
      if (stopping) return stopping;
      stopped = true;
      stopping = (async () => {
        const startedAt = Date.now();
        // A memo shorter than grant + handshake: let the socket open so the
        // buffered audio is sent before the vendor is asked to flush.
        if (!open) {
          await Promise.race([
            opened,
            new Promise<void>((resolve) => setTimeout(resolve, FLUSH_BASE_MS)),
          ]);
        }
        socket.finish();
        // The vendor works through a replayed backlog at least in real time,
        // so the extra allowance shrinks as the session runs on after replay.
        const backlogMs = Math.max(0, replayedMs - (Date.now() - replayedAt));
        const bound = Math.max(
          0,
          Math.min(FLUSH_MAX_MS, FLUSH_BASE_MS + backlogMs) -
            (Date.now() - startedAt),
        );
        let timer: ReturnType<typeof setTimeout> | undefined;
        return Promise.race([
          closed.then(() => true),
          new Promise<boolean>((resolve) => {
            timer = setTimeout(() => {
              socket.abort();
              resolve(false);
            }, bound);
          }),
        ]).finally(() => clearTimeout(timer));
      })();
      return stopping;
    },
  };
}
